/**
 * usePlacementUnderwriting - TanStack Query hooks for the Ausschreibungs-Screen Tab 5b (Underwriting /
 * Versicherer-Rueckmeldung, Prozessschritte 19-20; P2 Placement Slice 14). live mode hits
 * apiClient.placements.*Underwriting; mock mode resolves a fixture (Helvetia Antrag eingereicht, a
 * carrier reply awaiting classification, one open E6 condition round via the portal). The server owns
 * every gate; the UI only triggers + renders. While a send is running the query refetches every 5 s.
 *
 * [Source: apps/web/src/features/placements/usePlacementAntrag.ts] (live/mock + qk + invalidate idiom)
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../lib/api-client';
import { qk } from '../../lib/query-keys';
import { isLiveData } from '../../lib/data-mode';
import {
  UW_RUNDE_ART_LABELS,
  UW_RUNDE_STATUS_LABELS,
  type UnderwritingResponse,
  type UwAlternativeBody,
  type UwBedingungenBody,
  type UwDeckungBody,
  type UwEntscheidBody,
  type UwFallbackBody,
  type UwPoliceErhaltenBody,
  type UwRundeEntscheidBody,
} from '@brokly/shared';

function mockUnderwriting(id: string): UnderwritingResponse {
  return {
    placement_id: id,
    placement_number: 'PL-2026-000001',
    status: 'entschieden',
    closure_status: 'offen',
    branchen_label: 'Betriebshaftpflicht',
    kunde_name: 'Mueller AG',
    antrag: {
      id: 'apd-mock-1',
      version_no: 1,
      status: 'eingereicht',
      status_label: 'Eingereicht',
      status_changed_at: '2026-11-20T09:00:00.000Z',
      carrier_uid: 'CHE-111.111.111',
      carrier_name: 'Helvetia',
      carrier_logo_url: null,
      submission_id: 'sub-mock-1',
      submission_number: 'SUB-2026-000484-HAFT',
      offer_id: 'offd-helvetia',
      begin_date: '2027-01-01',
      praemie_jahr: 4850,
      versicherungssumme: 5000000,
      selbstbehalt: 500,
      vertragsdauer_monate: 12,
      submitted_at: '2026-11-20T09:00:00.000Z',
      submitted_to_email: 'offerten@helvetia.ch',
      vorgaenger_application_id: null,
      alternative_runde_id: null,
    },
    carrier_reply: { received_at: '2026-11-24T08:00:00.000Z', communication_id: 'comm-mock-reply', task_id: 'task-mock-reply', task_open: true },
    entscheid: null,
    deckung: null,
    police: null,
    fallback: null,
    runden: [
      {
        id: 'r-mock-1',
        runde_no: 1,
        art: 'bedingungen_geaendert',
        art_label: UW_RUNDE_ART_LABELS.bedingungen_geaendert,
        status: 'offen',
        status_label: UW_RUNDE_STATUS_LABELS.offen,
        status_changed_at: '2026-11-25T09:00:00.000Z',
        bedingungen_text: 'Erhoehter Selbstbehalt bei Wasserschaeden.',
        carrier_communication_id: 'comm-mock-reply',
        alternative: null,
        delta: [
          { feld: 'praemie_jahr', label: 'Jahrespraemie', alt: 4850, neu: 5120 },
          { feld: 'versicherungssumme', label: 'Versicherungssumme', alt: 5000000, neu: 5000000 },
          { feld: 'selbstbehalt', label: 'Selbstbehalt', alt: 500, neu: 1000 },
          { feld: 'begin_date', label: 'Versicherungsbeginn', alt: '2027-01-01', neu: null },
          { feld: 'vertragsdauer_monate', label: 'Vertragsdauer (Monate)', alt: 12, neu: null },
        ],
        geaenderte_felder: ['praemie_jahr', 'selbstbehalt'],
        ausschluesse_neu: [],
        zustellung: {
          channel: 'portal',
          channel_label: 'Kundenportal',
          zugestellt_at: '2026-11-25T09:00:00.000Z',
          zugestellt_by_name: 'Max Broker',
          empfaenger: null,
          portal_delivery_id: 'del-mock-1',
          email_id: null,
          frist_date: '2026-12-08',
          ueberfaellig: false,
          eskaliert_at: null,
          task_id: null,
        },
        entscheid: null,
        erfasst_by_name: 'Max Broker',
        created_at: '2026-11-25T09:00:00.000Z',
      },
    ],
    offene_runde_id: 'r-mock-1',
    akzeptierte_alternative_runde_id: null,
    alternativen: [],
    alternativen_hinweis: null,
    e7_task_id: null,
    kanal_vorschlag: 'portal',
    portal_verfuegbar: true,
    kunde_email: 'info@mueller-ag.example',
    frist_vorschlag: '2026-12-08',
    versand_laeuft: false,
    versand_gesperrt: false,
    aktionen: {
      annehmen: false,
      ablehnen: true,
      bedingungen_erfassen: false,
      runde_entscheid_erfassen: true,
      alternative_vorschlagen: false,
      fallback_erfassen: false,
      abbrechen: false,
      antrag_vorbereiten: false,
      deckung_bestaetigen: false,
      police_erhalten: false,
    },
    hinweise: [],
  };
}

export function usePlacementUnderwriting(id: string, enabled = true) {
  const live = isLiveData();
  return useQuery<UnderwritingResponse>({
    queryKey: qk.placementUnderwriting(id, live ? 'live' : 'mock'),
    queryFn: () => (live ? apiClient.placements.getUnderwriting(id) : Promise.resolve(mockUnderwriting(id))),
    enabled,
    refetchInterval: (query) => (live && query.state.data?.versand_laeuft ? 5000 : false),
    ...(live ? {} : { initialData: () => mockUnderwriting(id), staleTime: Infinity }),
  });
}

function useUwInvalidate(id: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['placements', 'underwriting', id] });
    void qc.invalidateQueries({ queryKey: ['placements', 'antrag', id] });
    void qc.invalidateQueries({ queryKey: ['placements', 'detail', id] });
  };
}

export function useAnnehmenUw(id: string) {
  const live = isLiveData();
  const invalidate = useUwInvalidate(id);
  return useMutation({
    mutationFn: (body: UwEntscheidBody) => (live ? apiClient.placements.annehmenUnderwriting(id, body) : Promise.resolve(mockUnderwriting(id))),
    onSuccess: () => invalidate(),
  });
}

export function useAblehnenUw(id: string) {
  const live = isLiveData();
  const invalidate = useUwInvalidate(id);
  return useMutation({
    mutationFn: (body: UwEntscheidBody) => (live ? apiClient.placements.ablehnenUnderwriting(id, body) : Promise.resolve(mockUnderwriting(id))),
    onSuccess: () => invalidate(),
  });
}

export function useBedingungenUw(id: string) {
  const live = isLiveData();
  const invalidate = useUwInvalidate(id);
  return useMutation({
    mutationFn: (body: UwBedingungenBody) => (live ? apiClient.placements.bedingungenUnderwriting(id, body) : Promise.resolve(mockUnderwriting(id))),
    onSuccess: () => invalidate(),
  });
}

export function useRundeEntscheidUw(id: string) {
  const live = isLiveData();
  const invalidate = useUwInvalidate(id);
  return useMutation({
    mutationFn: ({ rundeId, body }: { rundeId: string; body: UwRundeEntscheidBody }) =>
      live ? apiClient.placements.rundeEntscheidUnderwriting(id, rundeId, body) : Promise.resolve(mockUnderwriting(id)),
    onSuccess: () => invalidate(),
  });
}

export function useAlternativeUw(id: string) {
  const live = isLiveData();
  const invalidate = useUwInvalidate(id);
  return useMutation({
    mutationFn: (body: UwAlternativeBody) => (live ? apiClient.placements.alternativeUnderwriting(id, body) : Promise.resolve(mockUnderwriting(id))),
    onSuccess: () => invalidate(),
  });
}

export function useFallbackUw(id: string) {
  const live = isLiveData();
  const invalidate = useUwInvalidate(id);
  return useMutation({
    mutationFn: (body: UwFallbackBody) => (live ? apiClient.placements.fallbackUnderwriting(id, body) : Promise.resolve(mockUnderwriting(id))),
    onSuccess: () => invalidate(),
  });
}

export function useDeckungUw(id: string) {
  const live = isLiveData();
  const invalidate = useUwInvalidate(id);
  return useMutation({
    mutationFn: (body: UwDeckungBody) => (live ? apiClient.placements.deckungUnderwriting(id, body) : Promise.resolve(mockUnderwriting(id))),
    onSuccess: () => invalidate(),
  });
}

export function usePoliceErhaltenUw(id: string) {
  const live = isLiveData();
  const invalidate = useUwInvalidate(id);
  return useMutation({
    mutationFn: (body: UwPoliceErhaltenBody) => (live ? apiClient.placements.policeErhaltenUnderwriting(id, body) : Promise.resolve(mockUnderwriting(id))),
    onSuccess: () => invalidate(),
  });
}
